import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaClipboardCheck,
  FaDownload,
  FaEye,
  FaFileInvoiceDollar,
  FaHistory,
  FaLink,
  FaUniversity,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmModal from "../components/ConfirmModal";
import { downloadCsv } from "../utils/csv";
import {
  buildSettlementTimelineRows,
  formatDateTime,
  useSettlementRequests,
  useWalletAudit,
  useWalletTransactionsByRequest,
} from "../hooks/useWalletAdminData";
import {
  adminUpdateSettlement,
  createIdempotencyKey,
} from "../services/walletAdminService";
import { canMutateWallet, canViewWalletModule } from "../utils/walletPermissions";
import "./walletAdmin.css";

function money(v) {
  return Number(v || 0).toFixed(2);
}

const STATUS_OPTIONS = [
  "all",
  "requested",
  "on_hold",
  "processing",
  "credited",
  "rejected",
  "cancelled",
  "failed",
];

const ACTION_OPTIONS = [
  { value: "processing", label: "Mark Processing" },
  { value: "on_hold", label: "Mark On Hold" },
  { value: "credited", label: "Mark Credited" },
  { value: "rejected", label: "Mark Rejected" },
  { value: "cancelled", label: "Mark Cancelled" },
  { value: "failed", label: "Mark Failed" },
];

function isTerminalStatus(status) {
  const s = String(status || "").toLowerCase();
  return ["credited", "rejected", "cancelled", "failed"].includes(s);
}

export default function WithdrawRequests() {
  const navigate = useNavigate();
  const allowed = canViewWalletModule();
  const canMutate = canMutateWallet();

  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("processing");
  const [adminNote, setAdminNote] = useState("");
  const [settlementTxnId, setSettlementTxnId] = useState("");
  const [utrId, setUtrId] = useState("");
  const [settlementAt, setSettlementAt] = useState("");
  const [finalAmount, setFinalAmount] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  const { rows, loading, error } = useSettlementRequests({
    status,
    role,
    phone,
    amountMin,
    amountMax,
    dateFrom,
    dateTo,
  });

  const linkedTx = useWalletTransactionsByRequest(selected?.userPhone, selected?.requestId || selected?.id);
  const auditState = useWalletAudit({
    requestId: selected?.requestId || selected?.id || "",
    phone: selected?.userPhone || "",
  });

  const csvRows = useMemo(() => (
    rows.map((r) => ({
      requestId: r.requestId || r.id,
      userPhone: r.userPhone || "",
      name: r.name || "",
      roleAtRequest: r.roleAtRequest || "",
      amount: r.amount ?? "",
      status: r.status || "",
      settlementStatus: r.settlementStatus || "",
      payoutAccountId: r.payoutAccountId || "",
      settlementTxnId: r.settlementTxnId || "",
      utrId: r.utrId || "",
      finalAmount: r.finalAmount || "",
      adminNote: r.adminNote || "",
      createdAt: formatDateTime(r.createdAt),
      updatedAt: formatDateTime(r.updatedAt),
    }))
  ), [rows]);

  const selectedTimeline = useMemo(
    () => buildSettlementTimelineRows(selected),
    [selected]
  );

  if (!allowed) {
    return (
      <div className="wallet-card wallet-page">
        <h3 style={{ marginTop: 0 }}>Access Denied</h3>
        <p>Only admin/super_admin can access Settlement Requests.</p>
      </div>
    );
  }

  const resetActionForm = () => {
    setAction("processing");
    setAdminNote("");
    setSettlementTxnId("");
    setUtrId("");
    setSettlementAt("");
    setFinalAmount("");
  };

  const prepareActionPayload = () => {
    const payload = {};
    if (adminNote.trim()) payload.adminNote = adminNote.trim();

    if (action === "credited") {
      const amount = Number(finalAmount || selected?.amount || 0);
      if (!(amount > 0)) {
        throw new Error("Final amount must be > 0 for credited");
      }
      if (!settlementTxnId.trim()) {
        throw new Error("Settlement transaction ID is required");
      }
      if (!utrId.trim()) {
        throw new Error("UTR ID is required");
      }
      if (!settlementAt) {
        throw new Error("Settlement date/time is required");
      }
      payload.finalAmount = amount;
      payload.settlementTxnId = settlementTxnId.trim();
      payload.utrId = utrId.trim();
      payload.settlementAt = new Date(settlementAt).toISOString();
    }

    if (["on_hold", "rejected", "cancelled", "failed"].includes(action) && !adminNote.trim()) {
      throw new Error("Admin note is required for this action");
    }
    return payload;
  };

  const runAction = async () => {
    if (!selected) return;
    try {
      const payload = prepareActionPayload();
      setLoadingAction(true);
      await adminUpdateSettlement({
        requestId: selected.requestId || selected.id,
        action,
        payload,
        idempotencyKey: createIdempotencyKey(`settlement_${action}`),
      });
      toast.success(`Request updated: ${action}`);
      setConfirm(null);
      resetActionForm();
    } catch (err) {
      const msg = err?.details || err?.message || err?.code || "Failed to update settlement";
      toast.error(String(msg));
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div className="wallet-page">
      <div className="wallet-title-row">
        <div>
          <button className="wallet-btn wallet-btn-light" onClick={() => navigate("/panel/wallets")}>
            <FaArrowLeft style={{ marginRight: 6 }} />
            Back to Manage Wallets
          </button>
          <h2 style={{ margin: "10px 0 0" }}>Settlement Requests</h2>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            Request lifecycle: requested → processing/on_hold → credited or rejected/cancelled/failed
          </div>
        </div>
        <button
          className="wallet-btn wallet-btn-light"
          onClick={() => downloadCsv(`settlement_requests_${status}.csv`, csvRows)}
        >
          <FaDownload style={{ marginRight: 6 }} />
          Export CSV
        </button>
      </div>

      <div className="wallet-card" style={{ marginBottom: 12 }}>
        <div className="wallet-filter-grid wallet-filter-grid-6">
          <select className="wallet-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s === "all" ? "All Statuses" : s}</option>
            ))}
          </select>
          <select className="wallet-select" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="artist">Artist</option>
            <option value="mentor">Mentor</option>
          </select>
          <input
            className="wallet-input"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <input
            className="wallet-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Min Amount"
            value={amountMin}
            onChange={(e) => setAmountMin(e.target.value)}
          />
          <input
            className="wallet-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Max Amount"
            value={amountMax}
            onChange={(e) => setAmountMax(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="wallet-input"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              className="wallet-input"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="wallet-card">
        {loading ? <div className="wallet-empty">Loading requests...</div> : null}
        {!loading && error ? <div className="wallet-empty">{error}</div> : null}
        {!loading && !error && !rows.length ? <div className="wallet-empty">No requests found.</div> : null}

        {!loading && !error && rows.length ? (
          <div className="wallet-table-wrap">
            <table className="wallet-table wallet-table-wide">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Settlement Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.requestId || r.id}</td>
                    <td>{r.userPhone} {r.name ? `| ${r.name}` : ""}</td>
                    <td>{r.roleAtRequest || "-"}</td>
                    <td>Rs {money(r.amount)}</td>
                    <td>
                      <span className={`wallet-chip chip-${String(r.status || "").toLowerCase()}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td>{r.settlementStatus || "-"}</td>
                    <td>{formatDateTime(r.createdAt)}</td>
                    <td>{formatDateTime(r.updatedAt)}</td>
                    <td>
                      <button
                        className="wallet-btn wallet-btn-light"
                        onClick={() => {
                          setSelected(r);
                          resetActionForm();
                        }}
                      >
                        <FaEye style={{ marginRight: 6 }} />
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {selected ? <div className="wallet-drawer-backdrop" onClick={() => setSelected(null)} /> : null}
      {selected ? (
        <aside className="wallet-drawer wallet-drawer-wide">
          <div className="wallet-title-row">
            <h3 style={{ margin: 0 }}>Settlement Detail</h3>
            <button className="wallet-btn wallet-btn-light" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>

          <div className="wallet-card" style={{ marginBottom: 12 }}>
            <h4 className="wallet-section-head">
              <FaFileInvoiceDollar />
              Request Snapshot
            </h4>
            <dl className="wallet-kv">
              <dt>Request ID</dt><dd>{selected.requestId || selected.id}</dd>
              <dt>User Phone</dt><dd>{selected.userPhone || "-"}</dd>
              <dt>Name</dt><dd>{selected.name || "-"}</dd>
              <dt>Role</dt><dd>{selected.roleAtRequest || "-"}</dd>
              <dt>Amount</dt><dd>Rs {money(selected.amount)}</dd>
              <dt>Status</dt><dd>{selected.status || "-"}</dd>
              <dt>Settlement Status</dt><dd>{selected.settlementStatus || "-"}</dd>
              <dt>Window</dt>
              <dd>
                {selected.settlementWindowMinHours || 24}h to {selected.settlementWindowMaxDays || 7}d
              </dd>
              <dt>ETA</dt>
              <dd>
                {formatDateTime(selected.settlementEtaFrom)} - {formatDateTime(selected.settlementEtaTo)}
              </dd>
              <dt>Settlement Txn ID</dt><dd>{selected.settlementTxnId || "-"}</dd>
              <dt>UTR</dt><dd>{selected.utrId || "-"}</dd>
              <dt>Settlement At</dt><dd>{formatDateTime(selected.settlementAt)}</dd>
              <dt>Final Amount</dt><dd>{selected.finalAmount ? `Rs ${money(selected.finalAmount)}` : "-"}</dd>
              <dt>Admin Note</dt><dd>{selected.adminNote || "-"}</dd>
            </dl>
          </div>

          <div className="wallet-card" style={{ marginBottom: 12 }}>
            <h4 className="wallet-section-head">
              <FaUniversity />
              Payout Account
            </h4>
            <dl className="wallet-kv">
              <dt>Method</dt><dd>{selected?.payoutAccount?.method || "-"}</dd>
              <dt>Holder Name</dt><dd>{selected?.payoutAccount?.holderName || selected?.payoutAccount?.accountHolder || "-"}</dd>
              <dt>UPI</dt><dd>{selected?.payoutAccount?.upiId || "-"}</dd>
              <dt>Bank</dt><dd>{selected?.payoutAccount?.bankName || "-"}</dd>
              <dt>Account Number</dt><dd>{selected?.payoutAccount?.accountNumber || "-"}</dd>
              <dt>IFSC</dt><dd>{selected?.payoutAccount?.ifsc || "-"}</dd>
              <dt>Primary ID</dt><dd>{selected?.payoutAccountId || "-"}</dd>
            </dl>
          </div>

          {!isTerminalStatus(selected.status) ? (
            <div className="wallet-card" style={{ marginBottom: 12 }}>
              <h4 className="wallet-section-head">
                <FaClipboardCheck />
                Update Settlement Status
              </h4>
              <select className="wallet-select" value={action} onChange={(e) => setAction(e.target.value)}>
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {action === "credited" ? (
                <div className="wallet-filter-grid" style={{ marginTop: 10 }}>
                  <input
                    className="wallet-input"
                    placeholder="Settlement Txn ID"
                    value={settlementTxnId}
                    onChange={(e) => setSettlementTxnId(e.target.value)}
                  />
                  <input
                    className="wallet-input"
                    placeholder="UTR ID"
                    value={utrId}
                    onChange={(e) => setUtrId(e.target.value)}
                  />
                  <input
                    className="wallet-input"
                    type="datetime-local"
                    value={settlementAt}
                    onChange={(e) => setSettlementAt(e.target.value)}
                  />
                  <input
                    className="wallet-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Final Amount"
                    value={finalAmount}
                    onChange={(e) => setFinalAmount(e.target.value)}
                  />
                </div>
              ) : null}

              <textarea
                className="wallet-textarea"
                placeholder="Admin Note"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                style={{ marginTop: 10 }}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button
                  className="wallet-btn wallet-btn-primary"
                  disabled={!canMutate}
                  onClick={() => {
                    setConfirm({
                      title: `Confirm ${action}`,
                      message: `Update ${selected.requestId || selected.id} to ${action}?`,
                    });
                  }}
                >
                  Submit Status Update
                </button>
              </div>
            </div>
          ) : (
            <div className="wallet-card" style={{ marginBottom: 12 }}>
              <strong>Terminal Request</strong>
              <div style={{ marginTop: 6, color: "#475569" }}>
                This request is already finalized as {selected.status}.
              </div>
            </div>
          )}

          <div className="wallet-card" style={{ marginBottom: 12 }}>
            <h4 className="wallet-section-head">
              <FaHistory />
              Timeline
            </h4>
            {!selectedTimeline.length ? <div className="wallet-empty">No timeline entries yet.</div> : null}
            {selectedTimeline.map((item) => (
              <div key={item.id} className="wallet-timeline-item">
                <div className="wallet-timeline-head">
                  <strong>{item.status || "-"}</strong>
                  <span>{formatDateTime(item.at)}</span>
                </div>
                <div style={{ color: "#475569", fontSize: 13 }}>
                  {item.note || "-"} {item.actorName ? `| ${item.actorName} (${item.actorRole || "-"})` : ""}
                </div>
                {item.settlementTxnId || item.utrId ? (
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Txn: {item.settlementTxnId || "-"} | UTR: {item.utrId || "-"}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="wallet-card" style={{ marginBottom: 12 }}>
            <h4 className="wallet-section-head">
              <FaLink />
              Linked Wallet Transactions
            </h4>
            {linkedTx.loading ? <div className="wallet-empty">Loading linked transactions...</div> : null}
            {!linkedTx.loading && !linkedTx.rows.length ? (
              <div className="wallet-empty">No linked transactions.</div>
            ) : null}
            {!linkedTx.loading && linkedTx.rows.length ? (
              <div className="wallet-table-wrap">
                <table className="wallet-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Direction</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Settlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedTx.rows.map((tx) => (
                      <tr key={tx.id}>
                        <td>{formatDateTime(tx.createdAt)}</td>
                        <td>{tx.type || "-"}</td>
                        <td>{tx.direction || "-"}</td>
                        <td>Rs {money(tx.amount)}</td>
                        <td>{tx.status || "-"}</td>
                        <td>{tx.settlementStatus || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <div className="wallet-card">
            <h4 className="wallet-section-head">
              <FaHistory />
              Audit Timeline
            </h4>
            {auditState.loading ? <div className="wallet-empty">Loading audit...</div> : null}
            {!auditState.loading && !auditState.rows.length ? (
              <div className="wallet-empty">No audit records found.</div>
            ) : null}
            {auditState.rows.map((row) => (
              <div key={row.id} className="wallet-audit-item">
                <div className="wallet-audit-line">
                  <strong>{row.actionType || "-"}</strong>
                  <span>{formatDateTime(row.createdAt)}</span>
                </div>
                <div className="wallet-audit-line">
                  <span>{row.reason || "-"}</span>
                  <span>{row.adminEmail || row.adminUid || "-"}</span>
                </div>
              </div>
            ))}
          </div>
        </aside>
      ) : null}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        loading={loadingAction}
        onCancel={() => setConfirm(null)}
        onConfirm={runAction}
      />

      <ToastContainer position="top-right" autoClose={2600} />
    </div>
  );
}
